// src/api/EmailPageApi.js

import axios from 'axios';
import { ERROR_CODES } from '../constants/errors'; // Import error codes

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export const fetchNewEmailsGmail = async (token, params) => {
  const gmailParams = {
    influencerId: params.influencerId,
    socialNetwork: params.socialNetwork, // This will be 'gmail_1' or 'gmail_2'
  };
  const url = `${baseUrl}/gmail/api/fetchNewGmailMessages`;
  try {
    const response = await axios.post(url, gmailParams, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500, // Allow 4xx and 5xx
    });

    if (response.status === 200) {
      const emails = response.data.emails;

      // 1. Log exactly what we received.
      console.log(
        '[DEBUG] Raw response.data.emails from /fetchNewGmailMessages:',
        emails,
      );

      // 2. GUARANTEE that we return an array.
      if (Array.isArray(emails)) {
        return emails;
      } else {
        console.error(
          "API Error: /fetchNewGmailMessages did not return an array in the 'emails' property. Received:",
          response.data,
        );
      }
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsGmail]: No new emails found.');
      return []; // No emails is a valid, non-error case
    } else {
      // Handle all other status codes as errors
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    // This catch block should only be for *network* errors or errors during the request setup.
    console.error('[fetchNewEmailsGmail] Network or request error:', error);
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      // The request was made but no response was received
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

// --- Microsoft 365 Wrappers ---

export const fetchNewEmailsMicrosoft = async (token, params) => {
  const msParams = {
    influencerId: params.influencerId,
    socialNetwork: params.socialNetwork, // 'microsoft365'
  };
  const url = `${baseUrl}/microsoft365/api/fetchNewMicrosoftMessages`;
  try {
    const response = await axios.post(url, msParams, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      const emails = response.data.emails;
      if (Array.isArray(emails)) {
        return emails;
      } else {
        console.error(
          'API Error: /fetchNewMicrosoftMessages did not return an array. Received:',
          response.data,
        );
      }
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsMicrosoft]: No new messages found.');
      return [];
    } else {
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchNewEmailsMicrosoft] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const fetchNewEmailsFacebook = async (token, params) => {
  const { influencerId } = params;
  const url = `${baseUrl}/meta/api/fetchNewFacebookMessages/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data.emails || [];
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsFacebook]: No new FB messages found.');
      return [];
    } else {
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchNewEmailsFacebook] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
};

export const fetchNewEmailsInstagram = async (token, params) => {
  const { influencerId } = params;
  const url = `${baseUrl}/meta/api/fetchNewInstagramMessages/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data.emails || [];
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsInstagram]: No new IG messages found.');
      return [];
    } else {
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchNewEmailsInstagram] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
};

export async function fetchNewEmailsWhatsapp(token, params) {
  const { influencerId } = params;
  const url = `${baseUrl}/whatsapp/api/fetchNewWhatsappMessages/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data.emails || [];
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsWhatsapp]: No new messages found.');
      return [];
    } else {
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchNewEmailsWhatsapp] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
}

/**
 * Retrieves new Telegram messages from DB or via Pyrogram.
 * Endpoint: /telegram/api/fetchNewTelegramMessages/<influencerId>
 */
export async function fetchNewEmailsTelegram(token, params) {
  const { influencerId } = params;
  const url = `${baseUrl}/telegram/api/fetchNewTelegramMessages/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return response.data.emails || [];
    } else if (response.status === 204) {
      console.warn('[fetchNewEmailsTelegram]: No new messages found.');
      return [];
    } else {
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[fetchNewEmailsTelegram] Request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
}

export const fetchNewEmails = async (token, params) => {
  console.log('[fetchNewEmailsApi] Params: ', params);
  try {
    if (
      params.socialNetwork === 'gmail_1' ||
      params.socialNetwork === 'gmail_2'
    ) {
      return await fetchNewEmailsGmail(token, params);
    } else if (params.socialNetwork === 'microsoft365') {
      return await fetchNewEmailsMicrosoft(token, params);
    } else if (params.socialNetwork === 'instagram') {
      return await fetchNewEmailsInstagram(token, params);
    } else if (params.socialNetwork === 'facebook') {
      return await fetchNewEmailsFacebook(token, params);
    } else if (params.socialNetwork === 'whatsapp') {
      return await fetchNewEmailsWhatsapp(token, params);
    } else if (params.socialNetwork === 'telegram') {
      return await fetchNewEmailsTelegram(token, params);
    } else {
      return {
        error: ERROR_CODES.INVALID_INPUT,
        message: 'Invalid social media type specified.',
      };
    }
  } catch (error) {
    console.error('[fetchNewEmails] Unexpected error:', error);
    return {
      error: ERROR_CODES.INTERNAL_SERVER_ERROR,
      message: error.message || 'An unexpected error occurred.',
    };
  }
};

export const generateEmailDraft = async (token, params) => {
  const url = `${baseUrl}/messages/api/generateEmailDraft`;
  try {
    const emailParams = {
      influencerId: params.influencerId,
      useAi: params.useAi,
      newEmails: params.newEmails || [],
      prompt: params.prompt || '',
    };
    const response = await axios.post(url, emailParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      // Return a consistent error object
      return {
        error: response.data.error,
        message: response.data.message,
        data: response.data.data,
      };
    }
  } catch (error) {
    console.error('[generateEmailDraft] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const translateEmailDraft = async (
  token,
  influencerId,
  activeChannel,
  draft,
  translationIdiom,
  country,
) => {
  const url = `${baseUrl}/messages/api/translateEmail`; // Assuming this endpoint exists
  try {
    const response = await axios.post(
      url,
      { influencerId, activeChannel, draft, translationIdiom, country },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return response.data; // Assuming a success message
    } else {
      // Handle non-200 responses as errors
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[translateEmailDraft] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const simpleTranslation = async (token, influencerId, emailData) => {
  const url = `${baseUrl}/messages/api/batchTranslateEmail`; // Assuming this endpoint exists
  try {
    const response = await axios.post(
      url,
      { influencerId, emailData },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return response.data; // Assuming a success message
    } else {
      // Handle non-200 responses as errors
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[simpleTranslation] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const sendGmail = async (token, params) => {
  const url = `${baseUrl}/gmail/api/sendGmail`; // Assuming this endpoint exists
  try {
    // Extract only Gmail-related parameters
    const gmailParams = {
      influencerId: params.influencerId,
      socialNetwork: params.socialNetwork,
      subject: params.subject,
      body: params.body,
      attachments: params.attachments || [],
      taskType: params.taskType,
      promoCode: params.promoCode,
    };
    const response = await axios.post(url, gmailParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return {
        status: true,
        message: response.data.message,
        messageId: response.data.messageId,
        parsing_info: response.data.parsing_info, // New field
      };
    } else {
      // Handle non-200 responses as errors
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendEmail] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const sendMicrosoftMsg = async (token, params) => {
  const url = `${baseUrl}/microsoft365/api/sendMicrosoftMessage`;
  try {
    const msParams = {
      influencerId: params.influencerId,
      socialNetwork: params.socialNetwork, // 'microsoft365'
      subject: params.subject,
      body: params.body,
      attachments: params.attachments || [],
    };
    const response = await axios.post(url, msParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return {
        status: true,
        message: response.data.message,
        messageId: response.data.messageId,
      };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendMicrosoftMsg] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * Sends a WhatsApp message via the Cloud API.
 * @param {string} token  - Bearer token for your app's authentication
 * @param {object} params - { influencerId, subject, body, attachments, etc. }
 * @returns {object}      - success or error object
 */
export async function sendWhatsappMsg(token, params) {
  const url = `${baseUrl}/whatsapp/api/sendWhatsappMsg`;
  try {
    const whatsParams = {
      influencerId: params.influencerId,
      subject: params.subject || '',
      body: params.body || '',
      // no attachments for now
    };

    const response = await axios.post(url, whatsParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendWhatsappMsg] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function sendFacebookMsg(token, params) {
  const url = `${baseUrl}/meta/api/sendFacebookMsg`;
  try {
    const fbParams = {
      influencerId: params.influencerId,
      subject: params.subject || '',
      body: params.body || '',
      // No attachments for now, ignoring them
    };
    const response = await axios.post(url, fbParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendFacebookMsg] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function sendInstagramMsg(token, params) {
  const url = `${baseUrl}/meta/api/sendInstagramMsg`;
  try {
    const instaParams = {
      influencerId: params.influencerId,
      subject: params.subject || '',
      body: params.body || '',
      // No attachments
    };
    const response = await axios.post(url, instaParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendInstagramMsg] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

/**
 * Sends a Telegram message.
 * Endpoint: /telegram/api/sendTelegramMsg
 */
export async function sendTelegramMsg(token, params) {
  const url = `${baseUrl}/telegram/api/sendTelegramMsg`;
  try {
    const tgParams = {
      influencerId: params.influencerId,
      subject: params.subject || '',
      body: params.body || '',
      attachments: params.attachments || [],
    };

    const response = await axios.post(url, tgParams, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      return { status: true, message: response.data.message };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[sendTelegramMsg] Request error:', error);
    if (error.response) {
      return {
        status: false,
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        status: false,
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return {
        status: false,
        error: ERROR_CODES.REQUEST_ERROR,
        message: error.message,
      };
    }
  }
}

export async function sendEmail(token, params) {
  switch (params.socialNetwork) {
    case 'gmail':
    case 'gmail_1':
    case 'gmail_2':
      return await sendGmail(token, params);
    case 'microsoft365': // <--- ADD THIS
      return await sendMicrosoftMsg(token, params);
    case 'facebook':
      return await sendFacebookMsg(token, params);
    case 'instagram':
      return await sendInstagramMsg(token, params);
    case 'whatsapp':
      return await sendWhatsappMsg(token, params);
    case 'telegram':
      return await sendTelegramMsg(token, params);
    default:
      return {
        status: false,
        error: ERROR_CODES.NOT_IMPLEMENTED,
        message: `No social network selected (eg Gmail,Telegram,etc). Please choose one in the dropdown menu and try again.`,
      };
  }
}

export const updateActiveChannel = async (
  token,
  influencerId,
  activeChannel,
) => {
  const url = `${baseUrl}/user/api/updateActiveChannel`; // Assuming this endpoint exists
  try {
    const response = await axios.post(
      url,
      { influencerId, activeChannel },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Assuming a success message
    } else {
      // Handle non-200 responses as errors
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[updateActiveChannel]] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const updateInfluencerStatus = async (
  token,
  influencerId,
  newInfluencerStatus,
) => {
  const url = `${baseUrl}/user/api/updateInfluencerStatus`; // Assuming this endpoint exists
  try {
    const response = await axios.post(
      url,
      { influencerId, newInfluencerStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return { status: true, message: response.data.message }; // Assuming a success message
    } else {
      // Handle non-200 responses as errors
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[updateInfluencerStatus]] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

export const obtainNewPartnerData = async (token, params) => {
  // This function will be specific to the channel for now.
  // We can add logic here to route to different endpoints if other platforms
  // have a similar feature in the future.
  let url = `${baseUrl}/gmail/api/extractPartnerData`;
  if (params.socialNetwork === 'microsoft365') {
    url = `${baseUrl}/microsoft365/api/extractPartnerData`;
  }

  try {
    const response = await axios.post(url, params, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      // Success, return the extracted data object
      return { status: true, data: response.data };
    } else {
      // Handle structured errors from the backend
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error('[obtainNewPartnerData] Network or request error:', error);
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};

/**
 * Saves the final, user-confirmed partner details to the database.
 * This updates the influencers table and syncs their associated products.
 */
export const saveConfirmedPartnerData = async (
  token,
  influencerId,
  finalData,
) => {
  const url = `${baseUrl}/user/api/confirmPartnerDetails`;

  try {
    const response = await axios.post(
      url,
      { influencerId, finalData },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 500,
      },
    );

    if (response.status === 200) {
      return { status: true, message: response.data.message };
    } else {
      return {
        status: false,
        error: response.data.error,
        message: response.data.message,
      };
    }
  } catch (error) {
    console.error(
      '[saveConfirmedPartnerData] Network or request error:',
      error,
    );
    if (error.response) {
      return {
        error: `HTTP_ERROR_${error.response.status}`,
        message: error.response.data.message || 'API request failed.',
      };
    } else if (error.request) {
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'No response received from server.',
      };
    } else {
      return { error: ERROR_CODES.REQUEST_ERROR, message: error.message };
    }
  }
};
